import { default as AbstractLink } from '@databases/content-link/AbstractLink';
import EntryLink from '@databases/content-link/EntryLink';
import { ViewComponentProps } from '@databases/fields/view/types';
import { EntryIconColor } from '@databases/icons';
import { messages } from '@databases/intl/messages';
import { logError } from '@databases/logs';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import {
  BrokenDatabase,
  Database,
  DatabaseFieldType,
  EntryWithContext,
} from '@databases/types/database';
import { isNonEmptyEntryLinkValue } from '@databases/utils/database/field-types/entryLink';
import {
  getDatabaseEntryOptional,
  getEntryName,
  isNonBrokenDatabase,
} from '@databases/utils/database/utils';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByIdOptional,
} from '@databases/utils/redux/databases/selectors';
import React, { FunctionComponent, useState, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { useScope } from '../../Scope';

const Container = styled.ul`
  list-style-type: none;
  padding: 0;
  width: 100%;

  > li + li {
    margin-top: 6px;
  }
`;

const StyledSkeletonItem = styled.div<{ fieldWidth: number }>`
  display: block;
  width: ${(props) => (props.fieldWidth ? Math.min(props.fieldWidth - 60, 100) : 100)}px;
  height: 8px;
  margin: 6px;
  background-color: var(--ds-skeleton, rgba(9, 30, 66, 0.04));
  border-radius: 16px;
`;

const preventDefault = (e: React.MouseEvent | React.FocusEvent) => {
  // Prevent propagation to inline edit
  e.stopPropagation();
};

const EntryLinkValueView: FunctionComponent<
  ViewComponentProps<DatabaseFieldType.ENTRY_LINK>
> = ({ contentId, field, value, noFocus, isReadonly }) => {
  const { databaseSpace, databaseId, contentId: databaseContentId } = field.settings;
  const [isLoading, setIsLoading] = useState(false);

  const scope = useScope();
  const intl = useIntl();
  const { formatMessage } = intl;

  const dispatch = defaultStoreUtils.useDispatch();

  useCallbackEffect(
    useCallback(async () => {
      setIsLoading(true);
      try {
        if (databaseContentId) {
          await dispatch(loadDatabaseByContentId(databaseContentId, intl));
        }
      } catch (err) {
        const error = new Error(
          `Could not load database with content id ${databaseContentId}: ${err}`
        );
        logError(error);
      } finally {
        setIsLoading(false);
      }
    }, [databaseContentId, dispatch, intl])
  );

  // add checks for content id
  const entryRefs = defaultStoreUtils.useEqualSelector((state) => {
    if ((!databaseSpace || !databaseId) && !databaseContentId) {
      return [];
    }
    let targetDatabase: Database | BrokenDatabase | undefined = undefined;

    if (databaseId) {
      targetDatabase = selectDatabaseByIdOptional(state, {
        spaceKey: databaseSpace?.key,
        id: databaseId,
      });
    } else if (databaseContentId) {
      targetDatabase = selectDatabaseByContentIdOptional(state, {
        contentId: databaseContentId,
      });
    }

    if (!targetDatabase) {
      return value.filter(isNonEmptyEntryLinkValue).flatMap(({ entryId, _id }) => [
        {
          entryId,
          title: formatMessage(messages['broken-link.broken-link-text']),
          _id,
        },
      ]);
    }

    const targetDatabaseDraft =
      targetDatabase &&
      isNonBrokenDatabase(targetDatabase) &&
      selectDatabaseDraftByContentId(state, {
        contentId: targetDatabase.contentId,
        scope,
        includeDeleted: false,
      });
    if (!targetDatabaseDraft) {
      return [];
    } else {
      return value.filter(isNonEmptyEntryLinkValue).flatMap(({ entryId, _id }) => {
        const entry = getDatabaseEntryOptional(targetDatabaseDraft, entryId);
        const getTitle = (entryWithContext: EntryWithContext) => {
          const entryName = getEntryName(entryWithContext, state);

          return entryName
            ? entryName
            : formatMessage(messages['unnamed-entities.unnamed-entry']);
        };

        return entry
          ? [
              {
                databaseSpaceKey: databaseSpace?.key,
                databaseId: databaseId,
                entryId,
                title: getTitle({ database: targetDatabaseDraft, entry }),
                _id,
                contentId: databaseContentId,
              },
            ]
          : [
              {
                entryId,
                title: formatMessage(messages['broken-link.broken-link-text']),
                _id,
              },
            ];
      });
    }
  });
  if (value.length === 0) {
    return null;
  } else if (isLoading) {
    return (
      <Container>
        <li>
          <AbstractLink noFocus iconBefore={<EntryIconColor label="" />}>
            <StyledSkeletonItem fieldWidth={field.width} />
          </AbstractLink>
        </li>
      </Container>
    );
  } else if (!value || entryRefs.length === 0) {
    return null;
  } else {
    return (
      <Container>
        {entryRefs.map((entryRef) => (
          <li key={entryRef._id}>
            <EntryLink
              entryRef={entryRef}
              noFocus={noFocus}
              onClick={preventDefault}
              onFocus={preventDefault}
              contentId={contentId}
              isReadonly={isReadonly}
            />
          </li>
        ))}
      </Container>
    );
  }
};

export default EntryLinkValueView;
